import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { ReturnRequestDto } from "@babackapp/private-api-client/types";

const formSchema = z.object({
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  phone: z.string().optional(),
  address1: z.string().max(230).optional(),
  address2: z.string().max(230).optional(),
  country: z
    .object({
      name: z.string().optional(),
      code: z.string(),
    })
    // Mirrors the API exactly, no more: shipping `country_code` is
    // @ApiCountryCode() (IsString + IsNotEmpty), so only an empty value is
    // refused. Demanding alpha-2 here would block the very returns this PR is
    // about — the ones still holding `CAN` or `France`, which the API accepts
    // and which no backfill has touched. The message sits on the object so it
    // lands on the `country` path, the one FormMessage is bound to.
    .refine((country) => country.code.length > 0, {
      message: "Sélectionnez un pays",
    }),
  zip: z.string().optional(),
  province_code: z.string().optional(),
  city: z.string().optional(),
});

export default function useEditShippingAddressForm(
  returnRequest: ReturnRequestDto,
) {
  const { shipping_address: shippingAddress } = returnRequest;

  return useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: shippingAddress.first_name ?? "",
      lastName: shippingAddress.last_name ?? "",
      address1: shippingAddress.address1 ?? "",
      address2: shippingAddress.address2 ?? "",
      zip: shippingAddress.zip ?? "",
      city: shippingAddress.city ?? "",
      // No invented default on either half. `FormCountrySelector` renders from
      // `code`, so an absent code shows the placeholder and the merchant has to
      // choose — which is what the previous "Fr" default did, by accident. The
      // stored name is kept as-is: blanking it would overwrite the record's
      // country on a save that never touched the field.
      country: {
        name: shippingAddress.country ?? "",
        code: shippingAddress.country_code ?? "",
      },
      province_code: shippingAddress.province_code ?? "",
      phone: shippingAddress.phone ?? "",
    },
  });
}
